import { useMemo } from "react";

import type { HealthReport } from "api/models/HealthReport";

export interface ColumnConfig {
  id: string | number;
  header: string;
  subheader: string;
  report: HealthReport;
}

interface UseColumnConfigurationResult {
  columns: ColumnConfig[];
  columnCount: number;
}

/**
 * Hook for managing dynamic column configuration based on selected health reports
 *
 * @param selectedHealthReports - Currently selected health reports to display as columns
 * @param formatDate - Function to format the date for column subheaders
 * @param getHeaderText - Function to get the translated header text (e.g., "Questionnaire X")
 * @returns Column configuration data
 */
export const useColumnConfiguration = (
  selectedHealthReports: HealthReport[],
  formatDate: (date: string) => string,
  getHeaderText: (reportNumber: number) => string
): UseColumnConfigurationResult => {
  const columns = useMemo<ColumnConfig[]>(() => {
    return selectedHealthReports.map(report => ({
      id: report.id,
      header: getHeaderText(report.health_report_number),
      subheader: formatDate(report.created_at),
      report,
    }));
  }, [selectedHealthReports, formatDate, getHeaderText]);

  const columnCount = useMemo(() => selectedHealthReports.length, [selectedHealthReports.length]);

  return {
    columns,
    columnCount,
  };
};
